import { ActivePlatformBuysFilterValues } from 'shared/src/domain/line-item/line-item-types';
import {
  calcProportion,
  PendingPropertyIndicator,
  ProgressBarContainer,
  PropertyIndicatorContainer,
  PropertyIndicatorLabel
} from './utils';
import { differenceInCalendarDays, isAfter, isBefore, isWithinInterval, subDays } from 'date-fns';
import { UTCDate } from '@date-fns/utc';
import { PowerSwitch } from '../../../icons/power-switch';
import clsx from 'clsx';
import { SetFilterValuesType } from '../table-utils';
import React from 'react';
import { isNil } from 'lodash';
import { CampaignTableRow } from 'shared/src/api/campaigns';
import { PendingMediaPlan, PendingPlatformBuy } from './amount-allocated';
import { isCampaignTotalsRow } from '../../../features/zoho-campaigns/all-campaigns';
import {
  AllLineItemTableData,
  isLineItemDetailRow,
  isLineItemTotalsRow
} from '../../../features/line-items/all-line-items';

type LineItemProps = { data: AllLineItemTableData | undefined };
type CampaignProps = { data: CampaignTableRow | undefined };

export function ActiveLineItemMediaPlans({ data }: LineItemProps) {
  if (isNil(data) || isLineItemTotalsRow(data) || !isLineItemDetailRow(data)) return null;
  if (isActiveOutsideDates(data)) return <ActiveOutsideDatesIndicator data={data} />;
  if (notActiveAndBeforeDates(data)) return <DaysUntilStartIndicator startDate={data.start_date} />;
  if (data.num_media_plans === 0) return <PendingPropertyIndicator label={<PendingMediaPlan />} />;
  return (
    <ActiveMediaPlansIndicator
      active={data.active_media_plans_count}
      total={data.num_media_plans}
    />
  );
}

export function ActiveCampaignMediaPlans({ data }: CampaignProps) {
  if (isNil(data) || isCampaignTotalsRow(data)) return null;
  if (notActiveAndBeforeDates(data) && data.flight_date)
    return <DaysUntilStartIndicator startDate={data.flight_date} />;
  if (data.total_media_plans_count === 0)
    return <PendingPropertyIndicator label={<PendingPlatformBuy />} />;
  return (
    <ActiveMediaPlansIndicator
      active={data.active_media_plans}
      total={data.total_media_plans_count}
    />
  );
}

function ActiveOutsideDatesIndicator({ data }: { data: AllLineItemTableData }) {
  if (isNil(data) || !isLineItemDetailRow(data)) return null;
  const yesterday = subDays(new UTCDate(), 1);
  const percentage = calcProportion(data.active_buys_outside_plan_count, data.num_media_plans);

  return (
    <PropertyIndicatorContainer className="active-alert-progress-indicator-shadow bg-red-500">
      <PropertyIndicatorLabel className="text-white">
        <PowerSwitch fill="#DBEAFE" />
        {isBefore(yesterday, data.start_date) ? 'Active before dates' : 'Active after dates'}
      </PropertyIndicatorLabel>
      <ProgressBarContainer>
        <div
          className={clsx('bg-linear-to-r h-1 rounded-[3px] from-[#EC3B3E] to-[#CF0205]')}
          style={{ width: `${percentage}%` }}
        />
      </ProgressBarContainer>
    </PropertyIndicatorContainer>
  );
}

function DaysUntilStartIndicator({ startDate }: { startDate: UTCDate }) {
  return (
    <PropertyIndicatorContainer className="bg-gray-100">
      <div className="flex items-center gap-1 text-[13px] leading-[20px] text-gray-600">
        <div className="font-bold">{differenceInCalendarDays(startDate, new UTCDate())}</div>
        days until start
      </div>
      <ProgressBarContainer />
    </PropertyIndicatorContainer>
  );
}

function ActiveMediaPlansIndicator({ active, total }: { active: number; total: number }) {
  const pct = calcProportion(active, total);
  return (
    <PropertyIndicatorContainer className={clsx(bgColor(pct), calcShadow(pct))}>
      <PropertyIndicatorLabel className={txtColor(pct)}>
        <PowerSwitch fill={iconColor(pct)} />
        {label(pct)}
      </PropertyIndicatorLabel>
      <ProgressBarContainer>
        <div
          className={clsx('bg-linear-to-r h-1 rounded-[3px] from-[#3B6FEC] to-[#2B00FF]')}
          style={{ width: `${pct}%` }}
        />
      </ProgressBarContainer>
    </PropertyIndicatorContainer>
  );
}

function isActiveOutsideDates(data: AllLineItemTableData) {
  const yesterday = subDays(new UTCDate(), 1);
  return (
    data.active_buys_outside_plan_count > 0 &&
    data.start_date &&
    data.end_date &&
    (isBefore(yesterday, data.start_date) || isAfter(yesterday, data.end_date))
  );
}

function notActiveAndBeforeDates(data: AllLineItemTableData | CampaignTableRow) {
  if ('active_media_plans' in data) {
    return (
      data.active_media_plans === 0 &&
      data.total_media_plans_count > 0 &&
      data.flight_date &&
      isBefore(new UTCDate(), data.flight_date)
    );
  } else {
    return (
      data.active_media_plans_count === 0 &&
      data.num_media_plans > 0 &&
      data.start_date &&
      data.end_date &&
      isBefore(new UTCDate(), data.start_date)
    );
  }
}

const calcShadow = (percentage: number) => {
  return percentage === 100 ? 'active-progress-indicator-shadow' : null;
};

export function ActiveLineItemMediaPlansTooltip({ data }: LineItemProps) {
  if (isNil(data) || isLineItemTotalsRow(data) || !isLineItemDetailRow(data)) return null;
  if (data.num_media_plans === 0) return 'No Media Plans with Platform Buys';

  return (
    <div className="flex flex-col items-center">
      <NumActive
        active={data.active_media_plans_count}
        total={data.num_media_plans}
        startDate={data.start_date}
        endDate={data.end_date}
      />
      <LineItemDate startDate={data.start_date} endDate={data.end_date} />
    </div>
  );
}

export function ActiveCampaignMediaPlansTooltip({ data }: CampaignProps) {
  if (isNil(data)) return null;
  if (isNil(data.flight_date) || isNil(data.end_date)) return 'No Dates Set';
  if (data.total_media_plans_linked === 0) return 'No Media Plans with Platform Buys';

  return (
    <div className="flex flex-col items-center">
      <NumActive
        active={data.active_media_plans}
        total={data.total_media_plans_count}
        startDate={data.flight_date}
        endDate={data.end_date}
      />
      <LineItemDate startDate={data.flight_date} endDate={data.end_date} />
    </div>
  );
}

type NumActiveProps = {
  active: number;
  total: number;
  startDate: UTCDate;
  endDate: UTCDate;
};

function NumActive({ active, total, startDate, endDate }: NumActiveProps) {
  if (active > 0 || isWithinInterval(new UTCDate(), { start: startDate, end: endDate })) {
    return (
      <div className="flex gap-1">
        <div className="font-semibold">{active}</div> of {total} actively spending as of{' '}
        {toLocalDate(subDays(new UTCDate(), 1))}
      </div>
    );
  }
  return null;
}

function LineItemDate({ startDate, endDate }: { startDate: UTCDate; endDate: UTCDate }) {
  return isBefore(new UTCDate(), startDate)
    ? `Line Item's start date is ${toLocalDate(startDate)}`
    : isAfter(new UTCDate(), endDate)
      ? `Line Item's end date was ${toLocalDate(endDate)}`
      : null;
}

export const activePlatformBuysFilterValues: SetFilterValuesType<ActivePlatformBuysFilterValues> = [
  { id: 'pending_plan', name: 'Pending plan' },
  { id: 'pending_start', name: 'Pending line item start' },
  { id: 'active_outside_dates', name: 'Active outside dates' },
  { id: 'some_active', name: 'Some active' },
  { id: 'all_active', name: 'All active' },
  { id: 'none_active', name: 'None active' }
];

function label(percentage: number) {
  return percentage === 100 ? 'All active' : percentage === 0 ? 'None active' : 'Some active';
}

function bgColor(percentage: number) {
  return percentage === 100 ? 'bg-blue-500' : percentage === 0 ? 'bg-gray-100' : 'bg-blue-200';
}

function txtColor(percentage: number) {
  return percentage === 100 ? 'text-white' : percentage === 0 ? 'text-gray-600' : 'text-blue-800';
}

function iconColor(percentage: number) {
  return percentage === 100 ? '#DBEAFE' : percentage === 0 ? '#6B7280' : '#3B82F6';
}

function toLocalDate(date: UTCDate) {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
